import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { api } from '../../../util/api';
import { notify } from "../../../util/toast";
import ActionButton from "../../../component/common/ActionButton";
import {
    Box,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    FormHelperText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import FormModal from '../../../component/common/FormModal';
import ComboBox from '../../../component/common/ComboBox';

function QrEventParticipantRequestForm() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [affiliation, setAffiliation] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [gender, setGender] = useState('');
    const [consent, setConsent] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [contactError, setContactError] = useState('');
    const [genderError, setGenderError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const history = useHistory();
    const { eventName } = useParams();
    const displayName = eventName.replaceAll('_', ' ');

    const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    const validateContact = (v) => /^\d{3}-\d{4}-\d{4}$/.test(v);

    const handleContactChange = (e) => {
        let v = e.target.value.replace(/[^0-9]/g, '');
        if (v.length > 3 && v.length <= 7) v = `${v.slice(0, 3)}-${v.slice(3)}`;
        else if (v.length > 7) v = `${v.slice(0, 3)}-${v.slice(3, 7)}-${v.slice(7, 11)}`;
        setContactNumber(v);
        setContactError(validateContact(v) ? '' : '000-0000-0000 형식으로 입력해 주세요.');
    };

    const handleSubmit = async () => {
        let valid = true;

        if (!consent) { notify('개인정보 이용에 동의해 주세요.', 'warning'); return; }

        if (!validateEmail(email)) {
            setEmailError('올바른 이메일 형식을 입력해 주세요.');
            valid = false;
        } else setEmailError('');

        if (!validateContact(contactNumber)) {
            setContactError('000-0000-0000 형식으로 입력해 주세요.');
            valid = false;
        } else setContactError('');

        if (!gender) {
            setGenderError('성별을 선택해 주세요.');
            valid = false;
        } else setGenderError('');

        if (!valid) return;

        const decodedEventName = displayName;
        setSubmitting(true);
        try {
            const responseData = await api.post(
                `${process.env.REACT_APP_API_REAL_URL}/public/qr-event-participant/register`,
                { eventName: decodedEventName, participant: { name, email, affiliation, contactNumber, gender, eventName: decodedEventName } }
            );
            notify('참가 신청이 성공적으로 접수되었습니다.', 'success')
            history.push('/qr-event-participant-my-view', { uuid: responseData, name, affiliation, contactNumber, email, eventName });
        } catch (error) {
            if (error.response) notify('중복 참가 신청이거나 유효하지 않은 정보입니다.', 'error')
            else if (error.request) notify('서버와의 통신에 문제가 발생했습니다.', 'error')
            else notify('요청 중 오류가 발생했습니다.', 'error')
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Container maxWidth="sm">
                <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                    <Stack spacing={0.5} sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px', color: 'text.primary' }}>
                            참가 신청
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{displayName}</Typography>
                    </Stack>

                    <Stack spacing={2}>
                        <TextField
                            label="성함"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                        />

                        <Box>
                            <TextField
                                label="이메일"
                                type="email"
                                placeholder="onnme@gmail.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError(validateEmail(e.target.value) ? '' : '올바른 이메일 형식을 입력해 주세요.');
                                }}
                                fullWidth
                                required
                                error={!!emailError}
                            />
                            {emailError && <FormHelperText error>{emailError}</FormHelperText>}
                        </Box>

                        <TextField
                            label="소속"
                            placeholder="없을 경우 X"
                            value={affiliation}
                            onChange={(e) => setAffiliation(e.target.value)}
                            fullWidth
                            required
                        />

                        <Box>
                            <TextField
                                label="연락처"
                                placeholder="010-0000-0000"
                                value={contactNumber}
                                onChange={handleContactChange}
                                fullWidth
                                required
                                error={!!contactError}
                            />
                            {contactError && <FormHelperText error>{contactError}</FormHelperText>}
                        </Box>

                        <Box>
                            <ComboBox
                                clearable={false}
                                placeholder="성별"
                                options={[
                                    { value: '남', label: '남' },
                                    { value: '여', label: '여' },
                                ]}
                                value={gender}
                                onChange={(v) => { setGender(v); setGenderError(''); }}
                            />
                            {genderError && <FormHelperText error>{genderError}</FormHelperText>}
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consent}
                                    onChange={() => setModalOpen(true)}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 13 }}>
                                    개인정보 이용에 동의합니다{' '}
                                    <Typography component="span" sx={{ fontSize: 13, color: '#2563eb', cursor: 'pointer' }} onClick={() => setModalOpen(true)}>
                                        (내용 보기)
                                    </Typography>
                                </Typography>
                            }
                        />

                        <ActionButton
                            variant="primary"
                            onClick={handleSubmit}
                            loading={submitting}
                            label={submitting ? '접수 중...' : '참가 신청'}
                            fullWidth
                            startIcon={false}
                            sx={{ py: 1.25 }}
                        />
                    </Stack>
                </Paper>
            </Container>

            {/* 개인정보 처리방침 모달 */}
            <FormModal
                open={modalOpen}
                onClose={() => { setConsent(true); setModalOpen(false); }}
                title="개인정보 이용 및 처리방침"
                maxWidth="sm"
                onSave={() => { setConsent(true); setModalOpen(false); }}
                saveLabel="동의하고 닫기"
                cancelLabel="닫기"
            >
                <Stack spacing={1.5} sx={{ fontSize: 14, color: 'text.secondary' }}>
                    <Typography variant="body2">
                        ㈜온앤미는 정보주체의 자유와 권리보호를 위해 「개인정보보호법」 및 관계 법령이 정한 바를 준수하고 있습니다.
                        이에 「개인정보보호법」 제 30조에 따라 용역업무 및 기타 대행업무 수행과정에서 습득한 개인정보 이용 및 처리기준을 다음과 같이 수립·공개합니다.
                    </Typography>
                    <Typography variant="body2">1. 정보 범위: 성명, 개인 연락처, 이메일, 주소, 아이디 등</Typography>
                    <Typography variant="body2">2. 이용 목적: 프로그램 모집 및 홍보, 이벤트 진행 등 용역업무 수행</Typography>
                    <Typography variant="body2">3. 이용 매체: 명단 등록·관리 웹서비스, 문서 등</Typography>
                    <Typography variant="body2">4. 보유 기간: 습득일 기준 3년</Typography>
                    <Typography variant="body2">5. 처리 위탁: 가명처리 관련, 코드 변환, 웹서비스 운영 및 유지관리 등</Typography>
                    <Typography variant="body2">6. 이 용 자: ㈜온앤미</Typography>
                    <Typography variant="body2">
                        《제 3자 제공에 관한 사항》 ㈜온앤미는 정보주체의 동의, 법률의 특별한 규정 등 「개인정보보호법」 제 17조 및 제 18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.
                    </Typography>
                </Stack>
            </FormModal>
        </Box>
    );
}

export default QrEventParticipantRequestForm;
